import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { ConfigContext, UserContext } from '../../context';
import {
  Button,
  ButtonType,
  cookieOptions,
  getOrganisationDomain,
  Modal,
  REFERRAL_COOKIE_KEY,
  ShareReferral,
} from '../../shared';

import classes from './ReferralModal.module.scss';

export const ReferralModal: FunctionComponent = () => {
  const { t } = useTranslation();

  const cookieKey = useMemo(() => `${REFERRAL_COOKIE_KEY}_${getOrganisationDomain()}`, []);

  const [cookies, setCookie] = useCookies([cookieKey]);

  const { referralModalSessionsCount = 0 } = { ...cookies[cookieKey] };

  const {
    organisationLogoUrl,
    meta: { title: appTitle },
    referral = {
      enabled: false,
      modal: {
        titleLocaliseKey: '',
        descriptionLocaliseKey: '',
        shareLocaliseKey: '',
        opensLimit: 0,
        sessionsLimit: 0,
      },
    },
  } = { ...useContext(ConfigContext).config.elements };

  const { isAnonymous } = useContext(UserContext).userProfile;

  const {
    enabled,
    modal: {
      titleLocaliseKey,
      descriptionLocaliseKey,
      shareLocaliseKey,
      opensLimit,
      sessionsLimit,
    },
  } = referral;

  const [referralModalOpened, setReferralModalOpened] = useState<boolean>(false);

  const [shareModalOpened, setShareModalOpened] = useState<boolean>(false);

  const onReferralModalClose = useCallback(() => setReferralModalOpened(false), []);

  const onShare = useCallback(() => {
    onReferralModalClose();
    setShareModalOpened(true);
  }, [onReferralModalClose]);

  const onDisable = useCallback(() => {
    onReferralModalClose();
    setCookie(cookieKey, { referralModalSessionsCount: 99999 }, cookieOptions());
  }, [cookieKey, onReferralModalClose, setCookie]);

  const title = t(titleLocaliseKey);

  const body = useMemo(
    () => (
      <div className={classes['referral-modal']}>
        <img className={classes['referral-modal__logo']} src={organisationLogoUrl} alt={appTitle} />

        <div className={classes['referral-modal__title']}>{title}</div>

        <div className={classes['referral-modal__description']}>{t(descriptionLocaliseKey)}</div>

        <Button type={ButtonType.primary} label={t(shareLocaliseKey)} onClick={onShare} />

        <div className={classes['referral-modal__links']}>
          <button
            type={'button'}
            className={classes['referral-modal__links-remind']}
            onClick={onReferralModalClose}
          >
            {t('common.remind-later')}
          </button>
          <button
            type={'button'}
            className={classes['referral-modal__links-disable']}
            onClick={onDisable}
          >
            {t('common.do-not-show-again')}
          </button>
        </div>
      </div>
    ),
    [
      appTitle,
      descriptionLocaliseKey,
      onDisable,
      onReferralModalClose,
      onShare,
      organisationLogoUrl,
      shareLocaliseKey,
      t,
      title,
    ]
  );

  useEffect(() => {
    const sessionsCount = referralModalSessionsCount + 1;

    if (sessionsCount > opensLimit) {
      return;
    }

    if (sessionsCount % sessionsLimit === 0) {
      setReferralModalOpened(true);
    }

    setCookie(cookieKey, { referralModalSessionsCount: sessionsCount }, cookieOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!enabled || isAnonymous) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={referralModalOpened}
        body={body}
        onClose={onReferralModalClose}
        contentStyle={{ width: '30rem' }}
        ariaLabel={title || t('shareReferral.title')}
        disableCloseByOverlay
        alignTop
      />
      <ShareReferral isOpen={shareModalOpened} onClose={() => setShareModalOpened(false)} />
    </>
  );
};
